import React, { useMemo } from "react";
import { createUseStyles } from "react-jss";
import { useScrollPercentage } from "../Block/useScrollPercentage";
import { OpenLayerMap } from "./OpenLayerMap";
import { useScrollyTellProps, useScrollytellState } from "../Admin/scrollytellContext";
import { updateSection } from "../Admin/service";
import { useSection } from "../Section/useSection";
import { normalizeMapConfig, buildSectionMapPatch, computeMapSectionMultiplier } from "./mapWaypoints";
import { computeDistanceStats, computeCameraState } from "./cameraChoreography";
import { ExpeditionHud } from "./ExpeditionHud";

const useStyle = createUseStyles({
  mapScroll: {
    width: "100%",
    position: "sticky",
    maxHeight: ({ maxHeight }) => maxHeight,
  },
  content: {
    width: "100%",
    position: "sticky",
    top: 0,
  },
});

export const MapScroll = ({
  zoom,
  sectionId,
  containersCount,
  contentHeight,
  isThumbnail,
  activeStepIndex = 0,
}) => {
  const { editDirections } = useScrollytellState();
  const threshold = 0;
  const root = typeof document !== "undefined" ? document.getElementById("scrollytell-content") : null;
  const option = root !== null ? { root, threshold } : { threshold };
  const [wrapperRef, percentage] = useScrollPercentage(option);
  const classes = useStyle({
    maxHeight: isThumbnail ? "100%" : "none",
  });
  const section = useSection(sectionId);
  const { story, onChange } = useScrollyTellProps();

  const normalizedConfig = useMemo(() => {
    return normalizeMapConfig(section);
  }, [section]);

  const distanceStats = useMemo(() => {
    return computeDistanceStats(
      normalizedConfig.waypoints,
      percentage,
      normalizedConfig.distanceUnit
    );
  }, [normalizedConfig.waypoints, percentage, normalizedConfig.distanceUnit]);

  const cameraState = useMemo(() => {
    return computeCameraState({
      waypoints: normalizedConfig.waypoints,
      progress: percentage,
      activeStepIndex,
      baseZoom: zoom || section?.zoom || 8,
      routeStyle: normalizedConfig.routeStyle,
    });
  }, [normalizedConfig.waypoints, normalizedConfig.routeStyle, percentage, activeStepIndex, zoom, section?.zoom]);

  const handleWaypointChange = (newWaypoints) => {
    const updatedConfig = {
      ...normalizedConfig,
      waypoints: newWaypoints,
    };
    const patch = buildSectionMapPatch(updatedConfig);
    onChange(updateSection(story, sectionId, patch));
  };

  const handleAdd = (newCoord) => {
    const currentWaypoints = normalizedConfig.waypoints;
    const newWp = {
      id: `wp-${Date.now()}`,
      coordinate: newCoord,
      title: `Stopp ${currentWaypoints.length + 1}`,
    };
    handleWaypointChange([...currentWaypoints, newWp]);
  };

  const handleChange = (newCoord) => {
    onChange(
      updateSection(story, sectionId, {
        mapCoords: newCoord,
      })
    );
  };

  return (
    <div
      className={classes.mapScroll}
      ref={wrapperRef}
      style={{
        height: "100%",
        minHeight: `${computeMapSectionMultiplier(containersCount) * contentHeight}px`,
      }}
    >
      <div
        className={classes.content}
        style={{
          height: contentHeight,
        }}
      >
        <OpenLayerMap
          mapConfig={normalizedConfig}
          progress={percentage}
          activeStepIndex={activeStepIndex}
          zoom={zoom || section?.zoom || 8}
          edit={editDirections}
          onWaypointChange={handleWaypointChange}
          onAdd={handleAdd}
          onChange={handleChange}
          from={section?.mapCoords?.from}
          to={section?.mapCoords?.to}
        />

        <ExpeditionHud
          waypoints={normalizedConfig.waypoints}
          activeWaypointIndex={cameraState.activeWaypointIndex}
          travelledDistance={distanceStats.travelledDistance}
          totalDistance={distanceStats.totalDistance}
          distanceUnit={normalizedConfig.distanceUnit}
          showHud={normalizedConfig.showHud && !isThumbnail}
        />
      </div>
    </div>
  );
};
